import { Info, type LucideProps } from "lucide-react";
import { type ComponentProps, useMemo } from "react";
import { useMotion } from "@/components/providers/motion-provider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface StatCardProps extends ComponentProps<"div"> {
	data: {
		title: string;
		data: number;
		icon: React.ForwardRefExoticComponent<
			Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
		>;
		additional: string;
		description?: string;
		formatter?: (value: number) => string;
	};
}
export default function StatCard({ className, data }: StatCardProps) {
	// * state for motion toggler
	const { motion: motionPref } = useMotion();
	// If motion is off, disable animation/transition
	const shouldReduceMotion = useMemo(() => motionPref === "off", [motionPref]);

	return (
		<TooltipProvider delayDuration={200}>
			<Card
				className={cn(
					"bg-card flex flex-col h-full",
					className,
					!shouldReduceMotion && "motion-preset-confetti motion-duration-1000",
				)}
			>
				<CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0 gap-x-3">
					<CardTitle className="flex items-center gap-1 text-sm font-medium min-h-8">
						<span className="text-pretty">{data.title}</span>

						{data.description && (
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										className="mt-0.5 rounded-sm p-0.5 hover:bg-accent transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring opacity-75"
										aria-label={`Info about ${data.title}`}
									>
										<Info className="size-3.5 text-muted-foreground" />
									</button>
								</TooltipTrigger>
								<TooltipContent
									side="top"
									className="max-w-[240px] leading-relaxed"
								>
									<p>{data.description}</p>
								</TooltipContent>
							</Tooltip>
						)}
					</CardTitle>

					<data.icon className="size-4 shrink-0" />
				</CardHeader>
				<CardContent className="mt-auto">
					<p className="text-2xl font-bold">
						{data.formatter ? data.formatter(data.data) : data.data}
					</p>
					<p className="text-xs text-muted-foreground mt-1">
						{data.additional}
					</p>
				</CardContent>
			</Card>
		</TooltipProvider>
	);
}
